import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { HttpAgent, Message } from "@ag-ui/client";
import { type LucideIcon, UsersRound } from "lucide-react";
import {
  SessionContextPanelType,
  useSessionContextPanel,
  useSessionContextPanelOptional,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { apiClient, apiMutate } from "~/utils/apiClient";
import { NEXTGEN_CHAT_EVENT, type NextgenChatEvent, trackNextgenChatEvent } from "./analytics";
import { formatCohortCount, summarizeCohort } from "./cohortSummary";
import { fetchMcpAppResource, type FetchedMcpAppResource } from "./fetchMcpAppResource";

// MCP servers whose apps render in the sidebar's Apps section instead of
// inline in the message stream. `useMcpAppEvents` skips these so a given app
// never renders in both places.
export const PANE_MCP_SERVER_IDS: ReadonlySet<string> = new Set(["cohort_explorer"]);

// Label for the sidebar's Apps section, one row per server rather than per
// app instance — all open cohorts group under a single "Cohorts" row.
export const MCP_APP_GROUP_LABELS: Readonly<Record<string, string>> = {
  cohort_explorer: "Cohorts",
};

// The icon of a server's group row, the same one its chat card shows.
export const MCP_APP_GROUP_ICONS: Readonly<Record<string, LucideIcon>> = {
  cohort_explorer: UsersRound,
};

// Per-server configuration for the pane's server-agnostic mechanics: how to
// recognize a server's state object among a tool result's possible shapes, how
// to derive a stable identity for it (so repeated edits update one tab), and
// how to refresh it on history rehydration. Adding a new pane-hosted server is
// a new entry here — no changes needed to the tab/coercion logic below.
interface McpAppMatch {
  itemId: string;
  title?: string;
}

interface McpAppConfig {
  match(obj: Record<string, unknown>): McpAppMatch | null;
  // Recognizes a "this instance was deleted" result (e.g. delete_cohort's
  // response) and returns its itemId so the pane can close/remove the tab,
  // rather than opening a tab for it.
  matchRemoved?(obj: Record<string, unknown>): string | null;
  refresh?: {
    tool: string;
    buildArgs(itemId: string): Record<string, unknown>;
  };
  // Agent-facing tool names whose results only read an instance. Their
  // results update the tab's state but do not count as a change to it.
  readOnlyTools?: ReadonlySet<string>;
  // True while the server is still computing part of the state (e.g. a
  // cohort's counts). The pane re-runs `refresh` until it turns false.
  isPending?(obj: Record<string, unknown>): boolean;
  // A short live detail for the instance's sidebar row, e.g. a patient count.
  describe?(tab: McpAppTab): string | null;
  // The analytics event reported when an instance is opened in the sidebar.
  opened?: { event: NextgenChatEvent; itemIdProperty: string };
}

const MCP_APP_CONFIGS: Readonly<Record<string, McpAppConfig>> = {
  cohort_explorer: {
    match: (obj) =>
      typeof obj.id === "number" && Array.isArray(obj.criteria)
        ? { itemId: String(obj.id), title: typeof obj.name === "string" ? obj.name : undefined }
        : null,
    matchRemoved: (obj) =>
      obj.deleted === true && typeof obj.cohort_id === "number" ? String(obj.cohort_id) : null,
    refresh: {
      tool: "get_cohort",
      buildArgs: (itemId) => ({ cohort_id: Number(itemId) }),
    },
    readOnlyTools: new Set(["cohort_explorer_get_cohort"]),
    isPending: (obj) => obj.pending_count === true,
    describe: (tab) =>
      isRecord(tab.state) ? formatCohortCount(summarizeCohort(tab.state), tab.refreshFailed) : null,
    opened: { event: NEXTGEN_CHAT_EVENT.COHORT_VIEW_OPENED, itemIdProperty: "cohort_id" },
  },
};

// How often a tab whose state is still pending is refreshed.
const PENDING_STATE_POLL_MS = 1000;
// Refreshes after which a still-pending state is reported as unavailable. A
// failed background computation leaves the state pending for good.
export const PENDING_STATE_MAX_POLLS = 120;

// Where the user (or the pane itself) opened an app instance from, for analytics.
export const MCP_APP_OPEN_SOURCE = {
  AUTO: "auto",
  CARD: "card",
  SIDEBAR: "sidebar",
} as const;
export type McpAppOpenSource = (typeof MCP_APP_OPEN_SOURCE)[keyof typeof MCP_APP_OPEN_SOURCE];

export const mcpAppTabKey = (serverId: string, itemId: string): string => `${serverId}:${itemId}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function isReadOnlyCall(serverId: string, toolName: string | undefined): boolean {
  return toolName != null && MCP_APP_CONFIGS[serverId]?.readOnlyTools?.has(toolName) === true;
}

// The name of the tool a call in the transcript invoked, if the call is there.
function toolCallName(messages: ReadonlyArray<Message>, toolCallId: string): string | undefined {
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const call = message.toolCalls?.find((tc) => tc.id === toolCallId);
    if (call) return call.function.name;
  }
  return undefined;
}

// The tab a group's row opens: the active one if it is in the group, otherwise
// the group's most recently opened tab.
export function currentGroupTab(
  apps: McpAppTab[],
  serverId: string,
  activeKey: string | null
): McpAppTab | undefined {
  const groupTabs = apps.filter((tab) => tab.serverId === serverId);
  return groupTabs.find((tab) => tab.key === activeKey) ?? groupTabs[groupTabs.length - 1];
}

/** Server-specific live detail for a tab's sidebar row, or null if the server has none. */
export function describeMcpAppTab(tab: McpAppTab): string | null {
  return MCP_APP_CONFIGS[tab.serverId]?.describe?.(tab) ?? null;
}

// Parses a tool result the same way `coerceState` would unwrap it (JSON string
// or already-parsed object) and checks it against a server's `matchRemoved`
// predicate. Deletion payloads are flat and not MCP-content-block-wrapped, so
// unlike `coerceState` this doesn't need to recurse into arrays/nested objects.
function matchRemovedId(raw: unknown, serverId: string): string | null {
  const config = MCP_APP_CONFIGS[serverId];
  if (!config?.matchRemoved) return null;
  let parsed: unknown = raw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return config.matchRemoved(parsed as Record<string, unknown>);
}

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface McpAppRegistryEntry {
  tool_call_name: string;
  server_id: string;
  resource_uri: string;
  title: string;
}

export interface McpAppTab {
  // Stable identity of an app instance. Two results from the same app instance
  // (e.g. edits to the same cohort) share a key and update one tab in place.
  key: string;
  serverId: string;
  resourceUri: string;
  title: string;
  // `html`/`url` are always populated together (see `fetchMcpAppResource`): undefined on both
  // = not fetched yet, null on both = fetch failed, otherwise exactly one is loaded.
  html: string | null | undefined;
  url: string | null | undefined;
  // The latest tool result, pushed into the iframe as its render state.
  state: unknown;
  // True while the tab's resource is loading or its live state is being refreshed.
  loading: boolean;
  // Bumped each time the agent changes the instance (a live tool result), so
  // views can highlight the change. Reads, refreshes and panel edits leave it alone.
  version: number;
  // The tool call whose result last changed the instance, or null if unknown.
  lastToolCallId: string | null;
  // Refreshes of a pending state since the agent last changed the instance.
  pendingPolls: number;
  // Set when refreshing a pending state fails, or the state stays pending for
  // PENDING_STATE_MAX_POLLS refreshes; the pane stops polling the tab.
  refreshFailed: boolean;
}

export interface McpAppsPaneState {
  tabs: McpAppTab[]; // open, in insertion order
  hidden: McpAppTab[]; // closed but restorable
  activeKey: string | null;
  setActive: (key: string) => void;
  close: (key: string) => void;
  restore: (key: string) => void;
  // Apply state returned by a panel-initiated tool call to the tab in place.
  applyLocalUpdate: (key: string, rawState: unknown) => void;
}

interface PaneInternal {
  byKey: Map<string, McpAppTab>;
  openOrder: string[];
  hiddenOrder: string[];
  activeKey: string | null;
}

const EMPTY_INTERNAL: PaneInternal = {
  byKey: new Map(),
  openOrder: [],
  hiddenOrder: [],
  activeKey: null,
};

interface McpAppsPaneContextValue {
  pane: PaneInternal;
  setPane: Dispatch<SetStateAction<PaneInternal>>;
  refreshing: ReadonlySet<string>;
  setRefreshing: Dispatch<SetStateAction<ReadonlySet<string>>>;
}

// Holds the pane's state above both `Chat` (which owns the agent/messages that
// drive it) and `SessionContextSidebar` (which renders it) — the two are
// siblings in the tree, so the state has to live in their shared ancestor,
// `ProjectLayout`.
const McpAppsPaneContext = createContext<McpAppsPaneContextValue | null>(null);

export const McpAppsPaneProvider = ({ children }: { children: React.ReactNode }) => {
  const [pane, setPane] = useState<PaneInternal>(EMPTY_INTERNAL);
  const [refreshing, setRefreshing] = useState<ReadonlySet<string>>(() => new Set());

  return (
    <McpAppsPaneContext.Provider value={{ pane, setPane, refreshing, setRefreshing }}>
      {children}
    </McpAppsPaneContext.Provider>
  );
};

// Applies a tool result to an existing tab in place, if it matches the tab's
// server state shape. Leaves the pane unchanged otherwise.
function applyStateToPane(prev: PaneInternal, key: string, rawState: unknown): PaneInternal {
  const existing = prev.byKey.get(key);
  if (!existing) return prev;
  const state = coerceState(rawState, existing.serverId);
  if (!state) return prev;
  const matched = MCP_APP_CONFIGS[existing.serverId]?.match(state);
  const byKey = new Map(prev.byKey);
  byKey.set(key, { ...existing, title: matched?.title || existing.title, state });
  return { ...prev, byKey };
}

// Moves a tab to the open tabs (if it was hidden) and makes it active.
function revealTab(prev: PaneInternal, key: string): PaneInternal {
  if (!prev.byKey.has(key)) return prev;
  const isHidden = prev.hiddenOrder.includes(key);
  return {
    ...prev,
    hiddenOrder: isHidden ? prev.hiddenOrder.filter((k) => k !== key) : prev.hiddenOrder,
    openOrder: isHidden ? [...prev.openOrder, key] : prev.openOrder,
    activeKey: key,
  };
}

function isPendingState(serverId: string, state: Record<string, unknown>): boolean {
  return MCP_APP_CONFIGS[serverId]?.isPending?.(state) === true;
}

function isAwaitingState(tab: McpAppTab): boolean {
  return !tab.refreshFailed && isRecord(tab.state) && isPendingState(tab.serverId, tab.state);
}

// Applies a poll result to the tab it was polled for. A result is dropped if
// the agent changed the instance while the poll ran, as it can be older than
// the tab's state.
function applyPolledState(
  prev: PaneInternal,
  polled: McpAppTab,
  update: (tab: McpAppTab) => McpAppTab
): PaneInternal {
  const existing = prev.byKey.get(polled.key);
  if (!existing || existing.version !== polled.version) return prev;
  const byKey = new Map(prev.byKey);
  byKey.set(polled.key, update(existing));
  return { ...prev, byKey };
}

// Calls a server's configured refresh tool for one instance.
function fetchFreshState(
  serverId: string,
  itemId: string,
  signal: AbortSignal | undefined
): Promise<unknown> {
  const refresh = MCP_APP_CONFIGS[serverId]?.refresh;
  if (!refresh) throw new Error(`MCP server ${serverId} has no refresh tool`);
  return apiMutate<unknown>(
    `${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`,
    "POST",
    { server_id: serverId, tool: refresh.tool, arguments: refresh.buildArgs(itemId) },
    { signal }
  );
}

interface OpenTabDeps {
  setPane: Dispatch<SetStateAction<PaneInternal>>;
  openPanel: (type: SessionContextPanelType, id?: string) => void;
  expandSidebar: () => void;
}

// Shows one instance in the sidebar: reveals and activates its tab, expands a
// collapsed sidebar, opens the Apps section, and reports the open.
function openMcpAppTab(
  { setPane, openPanel, expandSidebar }: OpenTabDeps,
  serverId: string,
  key: string,
  source: McpAppOpenSource
): void {
  setPane((prev) => revealTab(prev, key));
  expandSidebar();
  openPanel(SessionContextPanelType.APPS, serverId);
  const opened = MCP_APP_CONFIGS[serverId]?.opened;
  if (opened) {
    trackNextgenChatEvent(
      opened.event,
      {},
      { source, [opened.itemIdProperty]: key.slice(serverId.length + 1) }
    );
  }
}

function useMcpAppsPaneContext(): McpAppsPaneContextValue {
  const ctx = useContext(McpAppsPaneContext);
  if (!ctx) {
    throw new Error("useMcpAppsPaneContext must be used within a McpAppsPaneProvider");
  }
  return ctx;
}

// Recursively dig out the server-shaped object from a tool result, which may
// arrive as a JSON string, an MCP content-block array, or a nested object.
function coerceState(raw: unknown, serverId: string, depth = 0): Record<string, unknown> | null {
  if (depth > 6 || raw == null) return null;
  if (typeof raw === "string") {
    try {
      return coerceState(JSON.parse(raw), serverId, depth + 1);
    } catch {
      return null;
    }
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (
        item &&
        typeof item === "object" &&
        typeof (item as { text?: unknown }).text === "string"
      ) {
        const fromText = coerceState((item as { text: string }).text, serverId, depth + 1);
        if (fromText) return fromText;
      }
      const got = coerceState(item, serverId, depth + 1);
      if (got) return got;
    }
    return null;
  }
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (MCP_APP_CONFIGS[serverId]?.match(obj)) return obj;
    for (const value of Object.values(obj)) {
      const got = coerceState(value, serverId, depth + 1);
      if (got) return got;
    }
  }
  return null;
}

/**
 * Subscribes to MCP-app UI resources destined for the sidebar's Apps section,
 * one tab per app instance. Unlike `useMcpAppEvents` (inline, per-tool-call,
 * reset each run), this accumulates across runs and keys tabs by app instance
 * so repeated edits update a single tab in place. Called from `Chat`, where
 * the agent/messages driving it live; the tab state itself is held in
 * `McpAppsPaneContext` so `SessionContextSidebar` — a sibling of `Chat` in the
 * tree, not a descendant — can read and render it via `useMcpAppsPaneState`.
 */
export function useMcpAppsPaneSync(agent: HttpAgent, messages: ReadonlyArray<Message>): void {
  const { pane, setPane, setRefreshing } = useMcpAppsPaneContext();
  // A live tool call is a composer-triggered action, so it should bring the
  // sidebar to the Apps section to show its result — history rehydration
  // (below) is a passive restore and shouldn't steal focus the same way.
  const { openPanel, expandSidebar } = useSessionContextPanel();
  const resourceCache = useRef<Map<string, FetchedMcpAppResource | null>>(new Map());
  const inflight = useRef<Set<string>>(new Set());
  const pollInflight = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);
  // Guards the one-shot history rehydration per conversation.
  const hydratedRef = useRef(false);
  // Whether this conversation has shown any app instance yet: only the first
  // one expands a collapsed sidebar, so a later collapse by the user stays.
  const hasShownAppRef = useRef(false);

  const markRefreshing = useCallback((key: string, on: boolean) => {
    setRefreshing((prev) => {
      if (on === prev.has(key)) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // Patches the fetched (or failed) resource into any tab still awaiting it.
  // Shared by both the success and failure paths of `ensureResource` so a
  // failure surfaces in state instead of leaving tabs stuck loading forever.
  const patchResource = useCallback(
    (resourceUri: string, resource: FetchedMcpAppResource | null) => {
      setPane((prev) => {
        const byKey = new Map(prev.byKey);
        let changed = false;
        for (const [key, tab] of byKey) {
          if (tab.resourceUri === resourceUri && tab.url === undefined) {
            byKey.set(key, { ...tab, html: resource?.html ?? null, url: resource?.url ?? null });
            changed = true;
          }
        }
        return changed ? { ...prev, byKey } : prev;
      });
    },
    [setPane]
  );

  // Fetch a resource once (cached) and patch it into any tab awaiting it.
  const ensureResource = useCallback(
    (resourceUri: string, serverId: string) => {
      if (resourceCache.current.has(resourceUri) || inflight.current.has(resourceUri)) return;
      inflight.current.add(resourceUri);
      const params = new URLSearchParams({ server_id: serverId, uri: resourceUri });
      const fetchUrl = `${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`;
      fetchMcpAppResource(fetchUrl, { signal: abortRef.current?.signal })
        .then((resource) => {
          resourceCache.current.set(resourceUri, resource);
          patchResource(resourceUri, resource);
        })
        .catch(() => {
          resourceCache.current.set(resourceUri, null);
          patchResource(resourceUri, null);
        })
        .finally(() => inflight.current.delete(resourceUri));
    },
    [patchResource]
  );

  // Insert or update a tab, keyed by app instance (e.g. a cohort), so results
  // from any source — live events, panel edits, history hydration — converge
  // on one tab. `change` names the tool call the state came from, with
  // `toolCallId` null for a call that only read the instance. A live result
  // reveals and activates the tab; a live change also bumps its version.
  // Otherwise updating an existing instance leaves its open/hidden placement
  // alone. Returns the tab's key so callers can react to a specific instance.
  const upsertTab = useCallback(
    (
      serverId: string,
      resourceUri: string,
      state: unknown,
      fallbackTitle: string,
      change?: { toolCallId: string | null; live: boolean }
    ) => {
      const matched = MCP_APP_CONFIGS[serverId]?.match(state as Record<string, unknown>);
      const key = mcpAppTabKey(serverId, matched?.itemId ?? resourceUri);
      const title = matched?.title || fallbackTitle;
      const isLive = change?.live ?? false;
      const isLiveChange = isLive && change?.toolCallId != null;
      setPane((prev) => {
        const existing = prev.byKey.get(key);
        const byKey = new Map(prev.byKey);
        // `resourceCache` only has an entry once the fetch settles, so `.has()` distinguishes
        // "not fetched yet" (undefined, keeps the tab loading) from "fetch failed" (null).
        const cached = resourceCache.current.has(resourceUri)
          ? (resourceCache.current.get(resourceUri) ?? null)
          : undefined;
        byKey.set(key, {
          key,
          serverId,
          resourceUri,
          title,
          html: existing?.html ?? (cached === undefined ? undefined : (cached?.html ?? null)),
          url: existing?.url ?? (cached === undefined ? undefined : (cached?.url ?? null)),
          state,
          loading: existing?.loading ?? false,
          version: existing ? existing.version + (isLiveChange ? 1 : 0) : 0,
          lastToolCallId: change?.toolCallId ?? existing?.lastToolCallId ?? null,
          // A new change from the agent starts a fresh pending period.
          pendingPolls: isLiveChange ? 0 : (existing?.pendingPolls ?? 0),
          refreshFailed: isLiveChange ? false : (existing?.refreshFailed ?? false),
        });
        if (existing) return isLive ? revealTab({ ...prev, byKey }, key) : { ...prev, byKey };
        // A brand-new instance opens as a tab and becomes active.
        return {
          byKey,
          openOrder: [...prev.openOrder, key],
          hiddenOrder: prev.hiddenOrder,
          activeKey: key,
        };
      });
      ensureResource(resourceUri, serverId);
      return key;
    },
    [ensureResource, setPane]
  );

  // Fully removes a tab (open or hidden) — used when the underlying item was
  // deleted, so unlike `close` there's no restoring it from `hidden` afterward.
  const removeTab = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.byKey.has(key)) return prev;
        const byKey = new Map(prev.byKey);
        byKey.delete(key);
        const openOrder = prev.openOrder.filter((k) => k !== key);
        const hiddenOrder = prev.hiddenOrder.filter((k) => k !== key);
        const activeKey =
          prev.activeKey === key ? (openOrder[openOrder.length - 1] ?? null) : prev.activeKey;
        return { byKey, openOrder, hiddenOrder, activeKey };
      });
    },
    [setPane]
  );

  // Refreshes a tab whose state is still pending. A failure, or a state still
  // pending after PENDING_STATE_MAX_POLLS refreshes, marks the tab so polling
  // stops and the views can say the state is unavailable.
  const pollPendingTab = useCallback(
    async (tab: McpAppTab) => {
      const matched = MCP_APP_CONFIGS[tab.serverId]?.match(tab.state as Record<string, unknown>);
      if (!matched || pollInflight.current.has(tab.key)) return;
      pollInflight.current.add(tab.key);
      const signal = abortRef.current?.signal;
      try {
        const fresh = await fetchFreshState(tab.serverId, matched.itemId, signal);
        const state = coerceState(fresh, tab.serverId);
        if (!state) {
          throw new Error(`Refresh of ${tab.key} returned an unrecognized state`);
        }
        const pendingPolls = tab.pendingPolls + 1;
        const exhausted =
          isPendingState(tab.serverId, state) && pendingPolls >= PENDING_STATE_MAX_POLLS;
        if (exhausted) {
          console.error(`${tab.key} is still pending after ${pendingPolls} refreshes`);
        }
        setPane((prev) =>
          applyPolledState(prev, tab, (existing) => ({
            ...existing,
            title: MCP_APP_CONFIGS[tab.serverId]?.match(state)?.title || existing.title,
            state,
            pendingPolls,
            refreshFailed: exhausted,
          }))
        );
      } catch (error) {
        if (signal?.aborted) return;
        console.error(`Refreshing the pending state of ${tab.key} failed`, error);
        setPane((prev) =>
          applyPolledState(prev, tab, (existing) => ({ ...existing, refreshFailed: true }))
        );
      } finally {
        pollInflight.current.delete(tab.key);
      }
    },
    [setPane]
  );

  // Poll: the service computes some state (e.g. cohort counts) after the tool
  // returns and sends no event when it is done, so refresh pending tabs until
  // their state is complete. Any tab change restarts the timer.
  useEffect(() => {
    const due = [...pane.byKey.values()].filter(isAwaitingState);
    if (due.length === 0) return;
    const timer = setTimeout(() => {
      for (const tab of due) void pollPendingTab(tab);
    }, PENDING_STATE_POLL_MS);
    return () => clearTimeout(timer);
  }, [pane.byKey, pollPendingTab]);

  // Live: subscribe to `mcp_app_ui` events for pane servers during a run.
  useEffect(() => {
    setPane(EMPTY_INTERNAL);
    setRefreshing(new Set());
    resourceCache.current = new Map();
    inflight.current = new Set();
    pollInflight.current = new Set();
    hydratedRef.current = false;
    hasShownAppRef.current = false;
    abortRef.current = new AbortController();

    const sub = agent.subscribe({
      onCustomEvent: ({
        event,
        messages: runMessages,
      }: {
        event: unknown;
        messages: ReadonlyArray<Message>;
      }) => {
        const e = event as { name?: string; value?: Record<string, unknown> };
        if (e.name !== "mcp_app_ui") return;

        const v = e.value;
        if (!v?.tool_call_id || !v.resource_uri || !v.mcp_server_id) return;
        const serverId = v.mcp_server_id as string;
        if (!PANE_MCP_SERVER_IDS.has(serverId)) return;

        const resourceUri = v.resource_uri as string;

        const removedId = matchRemovedId(v.tool_result, serverId);
        if (removedId != null) {
          removeTab(`${serverId}:${removedId}`);
          return;
        }

        // Only a recognized-shape result opens/updates a tab; a non-renderable
        // result (e.g. an error from a rejected write) is ignored so it can't
        // spawn an empty panel.
        const state = coerceState(v.tool_result, serverId);
        if (!state) return;

        const toolCallId = v.tool_call_id as string;
        const toolName = toolCallName(runMessages, toolCallId);
        const key = upsertTab(serverId, resourceUri, state, (v.title as string) || "MCP App", {
          toolCallId: isReadOnlyCall(serverId, toolName) ? null : toolCallId,
          live: true,
        });
        if (!hasShownAppRef.current) {
          hasShownAppRef.current = true;
          openMcpAppTab(
            { setPane, openPanel, expandSidebar },
            serverId,
            key,
            MCP_APP_OPEN_SOURCE.AUTO
          );
          return;
        }
        // The group (e.g. "Cohorts") is what the sidebar shows a row for, not
        // a specific instance — panel.id is the serverId, not the tab key.
        openPanel(SessionContextPanelType.APPS, serverId);
      },
    });

    return () => {
      sub.unsubscribe();
      abortRef.current?.abort();
    };
  }, [agent, upsertTab, removeTab, openPanel, expandSidebar, setPane, setRefreshing]);

  // Rehydrate on page refresh: once history loads, rebuild a tab per app
  // instance from persisted tool results (newest snapshot wins), then fold in
  // the freshest server state via each server's configured refresh tool so
  // panel edits made after the last agent turn survive too. If the server was
  // restarted (item gone) the refresh 404s and the history snapshot stands.
  useEffect(() => {
    if (hydratedRef.current || messages.length === 0) return;
    hydratedRef.current = true;

    let cancelled = false;
    const signal = abortRef.current?.signal;

    (async () => {
      let registry: { entries: McpAppRegistryEntry[] };
      try {
        registry = await apiClient<{ entries: McpAppRegistryEntry[] }>(
          `${VITE_ANALYSIS_AGENT_API}/mcp-app-registry`,
          { signal }
        );
      } catch {
        // Transient failure (e.g. the analysis-agent wasn't ready yet) — reset the guard so
        // the next messages update (more history streaming in) gives hydration another shot,
        // instead of silently giving up for the rest of the mount's lifetime.
        hydratedRef.current = false;
        return;
      }
      if (cancelled) return;

      const entryByTool = new Map(
        registry.entries
          .filter((e) => PANE_MCP_SERVER_IDS.has(e.server_id))
          .map((e) => [e.tool_call_name, e] as const)
      );
      if (entryByTool.size === 0) return;

      const resultByCallId = new Map<string, string>();
      for (const m of messages) {
        const tm = m as { role?: string; toolCallId?: string; content?: string };
        if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
          resultByCallId.set(tm.toolCallId, tm.content);
        }
      }

      const seeds = new Map<
        string,
        { entry: McpAppRegistryEntry; state: Record<string, unknown>; toolCallId: string | null }
      >();
      for (const m of messages) {
        const calls = (m as { toolCalls?: { id: string; function: { name: string } }[] }).toolCalls;
        if (!calls) continue;
        for (const tc of calls) {
          const entry = entryByTool.get(tc.function.name);
          if (!entry) continue;
          const raw = resultByCallId.get(tc.id);
          if (raw === undefined) continue;
          const state = coerceState(raw, entry.server_id);
          if (!state) continue;
          const matched = MCP_APP_CONFIGS[entry.server_id]?.match(state);
          const key = mcpAppTabKey(entry.server_id, matched?.itemId ?? entry.resource_uri);
          // A later message in order wins; a read keeps the last change's call.
          const toolCallId = isReadOnlyCall(entry.server_id, tc.function.name)
            ? (seeds.get(key)?.toolCallId ?? null)
            : tc.id;
          seeds.set(key, { entry, state, toolCallId });
        }
      }
      if (cancelled || seeds.size === 0) return;

      hasShownAppRef.current = true;
      for (const { entry, state, toolCallId } of seeds.values()) {
        upsertTab(entry.server_id, entry.resource_uri, state, entry.title, {
          toolCallId,
          live: false,
        });
      }

      await Promise.all(
        [...seeds.entries()].map(async ([key, { entry, state }]) => {
          const config = MCP_APP_CONFIGS[entry.server_id];
          const matched = config?.match(state);
          if (!config?.refresh || !matched) return;
          markRefreshing(key, true);
          try {
            const fresh = await fetchFreshState(entry.server_id, matched.itemId, signal);
            if (cancelled) return;
            const coerced = coerceState(fresh, entry.server_id);
            if (!coerced) return;
            upsertTab(entry.server_id, entry.resource_uri, coerced, entry.title);
          } catch {
            // Server restarted or item gone: keep the history snapshot.
          } finally {
            markRefreshing(key, false);
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, upsertTab, markRefreshing]);
}

/**
 * Read-only projection of the pane's tabs plus the actions to switch/close/
 * restore them — what `SessionContextSidebar`'s Apps section and its detail
 * view render from. Backed by the same `McpAppsPaneContext` that
 * `useMcpAppsPaneSync` writes into.
 */
export function useMcpAppsPaneState(): McpAppsPaneState {
  const { pane, setPane, refreshing } = useMcpAppsPaneContext();

  const setActive = useCallback(
    (key: string) => {
      setPane((prev) => (prev.openOrder.includes(key) ? { ...prev, activeKey: key } : prev));
    },
    [setPane]
  );

  const close = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.openOrder.includes(key)) return prev;
        const openOrder = prev.openOrder.filter((k) => k !== key);
        const activeKey =
          prev.activeKey === key ? (openOrder[openOrder.length - 1] ?? null) : prev.activeKey;
        return {
          ...prev,
          openOrder,
          hiddenOrder: [...prev.hiddenOrder, key],
          activeKey,
        };
      });
    },
    [setPane]
  );

  const restore = useCallback(
    (key: string) => {
      setPane((prev) => {
        if (!prev.hiddenOrder.includes(key)) return prev;
        return {
          ...prev,
          hiddenOrder: prev.hiddenOrder.filter((k) => k !== key),
          openOrder: [...prev.openOrder, key],
          activeKey: key,
        };
      });
    },
    [setPane]
  );

  const applyLocalUpdate = useCallback(
    (key: string, rawState: unknown) => {
      // Not every panel-initiated tool call returns the server's full state — e.g.
      // cohort_explorer's get_plotly_visualizations/create_access_request/
      // create_child_dataset all return something else entirely. Only sync the tab
      // when the result actually matches this server's state shape; otherwise leave
      // the tab's state untouched.
      setPane((prev) => applyStateToPane(prev, key, rawState));
    },
    [setPane]
  );

  return useMemo(() => {
    const withLoading = (t: McpAppTab): McpAppTab => ({
      ...t,
      loading: refreshing.has(t.key) || t.url === undefined,
    });
    const tabs = pane.openOrder
      .map((k) => pane.byKey.get(k))
      .filter((t): t is McpAppTab => t != null)
      .map(withLoading);
    const hidden = pane.hiddenOrder
      .map((k) => pane.byKey.get(k))
      .filter((t): t is McpAppTab => t != null)
      .map(withLoading);
    return {
      tabs,
      hidden,
      activeKey: pane.activeKey,
      setActive,
      close,
      restore,
      applyLocalUpdate,
    };
  }, [pane, refreshing, setActive, close, restore, applyLocalUpdate]);
}

/** Opens one app instance in the sidebar from an entry point (e.g. the sidebar row). */
export function useOpenMcpAppTab(): (
  serverId: string,
  key: string,
  source: McpAppOpenSource
) => void {
  const { setPane } = useMcpAppsPaneContext();
  const { openPanel, expandSidebar } = useSessionContextPanel();
  return useCallback(
    (serverId, key, source) =>
      openMcpAppTab({ setPane, openPanel, expandSidebar }, serverId, key, source),
    [setPane, openPanel, expandSidebar]
  );
}

export interface McpAppTabLink {
  // Undefined until the pane has a tab for the instance.
  tab: McpAppTab | undefined;
  open: (source: McpAppOpenSource) => void;
}

/**
 * One instance's tab and an opener for it, for entry points in the message
 * stream. Null outside the pane providers (the read-only conversation views).
 */
export function useMcpAppTabLink(serverId: string, itemId: string): McpAppTabLink | null {
  const paneContext = useContext(McpAppsPaneContext);
  const panelContext = useSessionContextPanelOptional();
  if (!paneContext || !panelContext) return null;
  const key = mcpAppTabKey(serverId, itemId);
  const deps = {
    setPane: paneContext.setPane,
    openPanel: panelContext.openPanel,
    expandSidebar: panelContext.expandSidebar,
  };
  return {
    tab: paneContext.pane.byKey.get(key),
    open: (source) => openMcpAppTab(deps, serverId, key, source),
  };
}

/** Finds a server's state object in a raw tool result, or null if it has none. */
export function findMcpAppState(raw: unknown, serverId: string): Record<string, unknown> | null {
  return coerceState(raw, serverId);
}
